import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { CardList } from "../../../ui/Card";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import { AuthContext } from "../../../../context/AuthContext";
import {
  swalConfirm,
  swalError,
  swalSuccess,
  swalWarning,
} from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";
import MobileCapture from "../../../ui/MobileCapture";
import FileUploadSection from "../../../ui/FileUploadSection";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const ManageRubric = () => {
  const [rubrics, setRubrics] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newRubric, setNewRubric] = useState({
    name: "",
    file: null,
    text: "",
    capturedFile: null,
  });
  const { user } = useContext(AuthContext);

  const [editRubric, setEditRubric] = useState({
    id: "",
    name: null,
    text: "",
    file: null,
    fileName: "",
    capturedFile: null,
    original: {},
  });
  const [initialEditRubric, setInitialEditRubric] = useState(null);
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewText, setPreviewText] = useState("");
  const [isPreviewTextOpen, setIsPreviewTextOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false); // New state for capture mode
  const [captureFor, setCaptureFor] = useState(null); // 'create' or 'edit'

  const hasFetched = useRef(false);

  const userDate = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return new Date(date.getTime()).toLocaleString(navigator.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  }, []);

  const fetchRubrics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/rubric/get-all/${user._id}`,
        {
          credentials: "include",
        }
      );
      if (!response) return;

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to create rubrics");
      }
      const enhancedData = data.map((rubric) => ({
        ...rubric,
        formattedDate: userDate(rubric.createdAt),
      }));

      setRubrics(enhancedData);
      setError(null);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userDate, user]);

  useEffect(() => {
    if (hasFetched.current) return;
    fetchRubrics();
    hasFetched.current = true;
  }, [fetchRubrics]);

  const handleCreateRubric = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!newRubric.text && !newRubric.file) {
      swalError("Either text or file must be provided.");
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append("name", newRubric.name);

    if (newRubric.file) {
      formData.append("file", newRubric.file);
    } else if (newRubric.text) {
      formData.append("text", newRubric.text);
    }

    try {
      const response = await fetchWithAuth(`${serverUrl}/api/rubric/add`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!response) return;

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to create rubric");
      }
      const message = data.message || "Rubric created successfully.";
      swalSuccess(message);
      setNewRubric({
        name: "",
        file: null,
        text: "",
        capturedFile: null,
      });
      fetchRubrics();
    } catch (error) {
      swalError(error.message);
    } finally {
      setIsSubmitting(false);
      setIsCreateModalOpen(false);
    }
  };

  const handleDeleteRubric = async (rubricId) => {
    swalConfirm(
      "Are you sure you want to delete this rubric?",
      "",
      "Yes, delete it!",
      "No, Cancel"
    ).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetchWithAuth(
            `${serverUrl}/api/rubric/delete/${rubricId}`,
            {
              method: "DELETE",
              credentials: "include",
            }
          );
          if (!response) return;
          if (!response.ok) throw new Error("Failed to delete rubric");
          swalSuccess("Rubric deleted successfully.");
          fetchRubrics();
        } catch (err) {
          setError(err.message);
          swalError(err.message);
        }
      }
    });
  };

  const handleEditRubric = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("name", editRubric.name);

    if (editRubric.file) {
      formData.append("file", editRubric.file);
    } else if (editRubric.text) {
      formData.append("text", editRubric.text);
    }

    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/rubric/update/${editRubric.id}`,
        {
          method: "PUT",
          credentials: "include",
          body: formData,
        }
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to update rubric");
      const message = data.message || "Rubric updated successfully.";
      swalSuccess(message);
      setEditRubric({
        id: "",
        name: null,
        text: "",
        file: null,
        fileName: "",
        capturedFile: null,
        original: {},
      });
      setInitialEditRubric(null);
      setIsEditChanged(false);
      fetchRubrics();
    } catch (error) {
      swalError(error.message);
    } finally {
      setIsSubmitting(false);
      setIsEditModalOpen(false);
    }
  };

  const handlePreview = (rubric) => {
    if (rubric.url) {
      window.open(rubric.url, "_blank");
    } else if (rubric.text) {
      setPreviewText(rubric.text);
      setIsPreviewTextOpen(true);
    } else {
      swalWarning("No content to preview.");
    }
  };

  // Handle Create Input Change
  const handleCreateInputChange = (e) => {
    const { name, value, files } = e.target;
    setNewRubric((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
      ...(name === "file" && files && { capturedFile: null, text: "" }),
      ...(name === "text" && value && { capturedFile: null, file: null }),
    }));
  };

  // Handle Edit Input Change
  const handleEditInputChange = (e) => {
    const { name, value, files } = e.target;
    const updatedValue = files ? files[0] : value;
    setEditRubric((prev) => ({
      ...prev,
      [name]: updatedValue,
      ...(name === "file" && files && { capturedFile: null }), // Reset captured file when new file is selected
    }));

    if (initialEditRubric) {
      const originalValue = initialEditRubric[name];
      if (files) {
        setIsEditChanged(files[0] !== originalValue);
      } else {
        setIsEditChanged(updatedValue !== originalValue);
      }
    }
  };

  // Callback when capture is done
  const onCaptureComplete = (pdfFile) => {
    // Call the stored callback from FileUploadSection
    if (window.__fileUploadCallback) {
      window.__fileUploadCallback(pdfFile);
      window.__fileUploadCallback = null;
    }

    // Keep existing logic for backward compatibility
    if (captureFor === "create") {
      setNewRubric((prev) => ({
        ...prev,
        file: pdfFile,
        capturedFile: pdfFile,
        text: "",
      }));
      setIsCreateModalOpen(true);
    } else if (captureFor === "edit") {
      setEditRubric((prev) => ({
        ...prev,
        file: pdfFile,
        capturedFile: pdfFile,
      }));
      setIsEditChanged(true);
      setIsEditModalOpen(true);
    }
    setIsCapturing(false);
    setCaptureFor(null);
  };

  // Add close handler for capture
  const handleCloseCapture = () => {
    setIsCapturing(false);
    if (captureFor === "create") {
      setIsCreateModalOpen(true); // Reopen create modal when closing capture without completing
    } else if (captureFor === "edit") {
      setIsEditModalOpen(true); // Reopen edit modal when closing capture without completing
    }
    setCaptureFor(null);
  };

  // Render Rubric File Upload Section
  const renderRubricFileUpload = (isEdit = false) => {
    const rubric = isEdit ? editRubric : newRubric;
    const hasText = isEdit ? false : rubric.text;
    const hasOriginalFile = isEdit && rubric.original.fileName && !rubric.file;

    return (
      <div className="form-group">
        <label>
          <i className="fa-solid fa-asterisk requiredIcon"></i> Upload File
          {!isEdit && " or Enter Text"}
        </label>

        <FileUploadSection
          currentFileUrl={hasOriginalFile ? rubric.original.url : null}
          currentFileName={
            hasOriginalFile ? rubric.original.fileName.split("/").pop() : null
          }
          isSubmitting={isSubmitting}
          accept=".pdf,.png,.jpg,.jpeg"
          onFileChange={(file, isCaptured) => {
            if (isEdit) {
              setEditRubric((prev) => ({
                ...prev,
                file: file,
                capturedFile: isCaptured ? file : null,
              }));
              setIsEditChanged(true);
            } else {
              setNewRubric((prev) => ({
                ...prev,
                file: file,
                capturedFile: isCaptured ? file : null,
                text: "", // Clear text when file is selected
              }));
            }
          }}
          onCapture={(callback) => {
            setCaptureFor(isEdit ? "edit" : "create");
            setIsCapturing(true);
            if (isEdit) {
              setIsEditModalOpen(false);
            } else {
              setIsCreateModalOpen(false);
            }
            // Store callback for later use
            window.__fileUploadCallback = callback;
          }}
          onPreviewFile={(url) => {
            window.open(url, "_blank");
          }}
          selectedFile={rubric.file}
          isCaptured={rubric.capturedFile !== null}
          browseTitle="Choose File"
          browseDescription="Select PDF or image file from device"
          captureTitle="Capture with Camera"
          captureDescription="Take photos and convert to PDF"
          additionalDisableCondition={hasText} // Disable file upload when text is entered
        />

        <small className="helper-text">
          Upload a rubric file (pdf, jpg, png only) or capture using camera
          {!isEdit && " (only on mobile phone)"}
        </small>
      </div>
    );
  };

  const cardsData = [
    ...(!user?.sourceSystem
      ? [
          {
            id: "action-card",
            title: "New Rubric",
            onAction: () => setIsCreateModalOpen(true),
            isActionCard: true,
          },
        ]
      : []),
    ...rubrics.map((rubric) => ({
      id: rubric._id,
      title: rubric.name,
      description: (
        <>
          <p>Created On: {rubric.formattedDate}</p>
          <p>
            <a
              href="#!"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handlePreview(rubric);
              }}
              className="btn-rubric-paper"
            >
              <i className="fa-regular fa-eye"></i> Rubric
            </a>
          </p>
        </>
      ),
      children: (
        <div className="card__actions">
          {!user?.sourceSystem && (
            <button
              onClick={() => {
                setEditRubric({
                  id: rubric._id,
                  name: rubric.name,
                  fileName: rubric.fileName,
                  text: rubric.text,
                  file: null,
                  capturedFile: null,
                  original: {
                    name: rubric.name,
                    url: rubric.url,
                    fileName: rubric.fileName,
                    text: rubric.text,
                  },
                });
                setInitialEditRubric({
                  name: rubric.name,
                  text: rubric.text,
                  fileName: rubric.fileName,
                });
                setIsEditModalOpen(true);
                setIsEditChanged(false);
              }}
              className="btn-edit"
              aria-label="Edit Rubric"
            >
              <img src="/imgs/pencil_icon.svg" alt="edit-btn" width={25} />
            </button>
          )}
          <button
            onClick={() => handleDeleteRubric(rubric._id)}
            className="btn-delete"
            aria-label="Delete Rubric"
          >
            <img src="/imgs/trash_icon.svg" alt="delete-btn" width={25} />
          </button>
        </div>
      ),
    })),
  ];

  return (
    <>
      {/* Capture Full Screen - Show this instead of the main content when capturing */}
      {isCapturing ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 9999,
            background: "white",
          }}
        >
          <MobileCapture
            onComplete={onCaptureComplete}
            onClose={handleCloseCapture}
            mode="rubric"
          />
        </div>
      ) : (
        <>
          <div className="manage-container">
            {loading && <Loader />}
            {error && <div className="error-message">{error}</div>}
            {!loading && !error && (
              <>
                <CardList cards={cardsData} />
              </>
            )}
          </div>

          {/* Add Rubric Modal */}
          <Modal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
          >
            <h2>Add New Rubric</h2>
            <p className="modal-description" style={{ marginBottom: "1rem" }}>
              Fill in the details below to add a new rubric for your class.
            </p>
            <form onSubmit={handleCreateRubric} className="rubric-form">
              <div className="form-group">
                <label>
                  <i className="fa-solid fa-asterisk requiredIcon"></i> Rubric
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={newRubric.name}
                  required
                  placeholder="Enter a descriptive rubric name"
                  onChange={handleCreateInputChange}
                  disabled={isSubmitting}
                />
              </div>

              {renderRubricFileUpload(false)}

              <div className="form-group">
                <label>
                  Rubric Text <span className="optional-text">(Optional)</span>
                </label>
                <textarea
                  name="text"
                  value={newRubric.text}
                  placeholder="Enter rubric text here..."
                  disabled={
                    newRubric.file || newRubric.capturedFile || isSubmitting
                  }
                  onChange={handleCreateInputChange}
                  rows={4}
                />
                <small className="helper-text">
                  You can either upload a file, capture using camera, or enter
                  rubric text, not multiple options.
                </small>
              </div>
              {!isSubmitting ? (
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isSubmitting}
                >
                  <i className="fa-solid fa-plus"></i> Create Rubric
                </button>
              ) : (
                <Loader fontSize="50px" />
              )}
            </form>
          </Modal>

          {/* Edit Rubric Modal */}
          <Modal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
          >
            <h2>Edit Rubric</h2>
            <p className="modal-description" style={{ marginBottom: "1rem" }}>
              Update the details below to edit the rubric information.
            </p>
            <form onSubmit={handleEditRubric} className="rubric-form">
              <div className="form-group">
                <label>
                  <i className="fa-solid fa-asterisk requiredIcon"></i> Rubric
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={editRubric?.name || ""}
                  required
                  placeholder="Enter a descriptive rubric name"
                  onChange={handleEditInputChange}
                  disabled={isSubmitting}
                />
              </div>

              {editRubric.original.url && renderRubricFileUpload(true)}

              {editRubric.original.text && (
                <div className="form-group">
                  <label>
                    Edit Rubric Text{" "}
                    <span className="optional-text">(Optional)</span>
                  </label>
                  <textarea
                    name="text"
                    value={editRubric?.text || ""}
                    placeholder="Edit rubric text here..."
                    onChange={handleEditInputChange}
                    disabled={
                      editRubric.file || editRubric.capturedFile || isSubmitting
                    }
                    rows={4}
                  />
                  <small className="helper-text">
                    You can update the rubric text here.
                  </small>
                </div>
              )}
              {!isSubmitting ? (
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={!isEditChanged}
                  onClick={(e) => {
                    if (!isEditChanged) {
                      e.preventDefault();
                      swalWarning("No changes to be saved!");
                    }
                  }}
                >
                  <i className="fa-solid fa-floppy-disk"></i> Update Rubric
                </button>
              ) : (
                <Loader fontSize="50px" />
              )}
            </form>
          </Modal>

          {/* Preview Text Modal */}
          <Modal
            isOpen={isPreviewTextOpen}
            onClose={() => setIsPreviewTextOpen(false)}
          >
            <h2>Rubric Text</h2>
            <div className="rubric-text-preview">
              <p>{previewText}</p>
            </div>
          </Modal>
        </>
      )}
    </>
  );
};

export default ManageRubric;
